/**
 * DenseSingleEventCard — the BON-142 dense-mode fuller card rendered for a
 * calendar day with exactly ONE event: dot + bold title, a "venue · time"
 * subtitle, and a seats-left / price-per-seat row. A lone event has the
 * whole cell to itself, so it gets richer treatment than the multi-event
 * `CompactEventPill`. Opt-in via `dense` — see that component's doc comment.
 */
import { dotColor, hexToRgba } from './calendarEvent';
import type { CalendarEvent } from './calendarEvent';
import { EventBadge } from './EventBadge';

export interface DenseSingleEventCardProps {
  event: CalendarEvent;
  selected: boolean;
  onSelect: (id: string) => void;
}

export function DenseSingleEventCard({ event, selected, onSelect }: DenseSingleEventCardProps) {
  const color = dotColor(event.title);
  const priceLabel = event.tooltip?.priceLabel;
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={event.disabled}
      title={event.disabled ? event.disabledReason : undefined}
      onClick={() => onSelect(event.id)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '6px 8px',
        borderRadius: 6,
        border: `1px solid ${selected ? 'var(--nb-primary)' : 'transparent'}`,
        background: hexToRgba(color, 0.16),
        cursor: event.disabled ? 'not-allowed' : 'pointer',
        opacity: event.disabled ? 0.5 : 1,
        font: 'inherit',
        color: 'var(--nb-ink)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <i
          aria-hidden="true"
          style={{ flex: '0 0 auto', width: 7, height: 7, borderRadius: '50%', background: color }}
        />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {event.title}
        </span>
        {event.badge && <EventBadge label={event.badge} />}
      </span>
      <span style={{ display: 'block', fontSize: 10, color: 'var(--nb-muted)', marginTop: 2 }}>
        {event.subtitle}
      </span>
      <span
        style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 10.5 }}
      >
        <span style={{ color: event.disabled ? 'var(--nb-warn)' : 'var(--nb-muted)' }}>
          {event.disabled ? (event.disabledReason ?? event.seatsLabel) : event.seatsLabel}
        </span>
        {priceLabel && <span style={{ fontWeight: 700 }}>{priceLabel}</span>}
      </span>
    </button>
  );
}
